import { CHECKLIST_VERSION } from '../data/inspectionCatalogue';
import { severityToResponseSeverity } from '../lib/inspection';
import { supabase } from '../lib/supabase';
import type {
  AssessmentRecord,
  InspectionAnswer,
  InspectionCheck,
  InspectionResponseRecord,
} from '../types/inspection';

export async function startOrResumeAssessment(
  ownerId: string,
  propertyId: string,
): Promise<AssessmentRecord> {
  const { data: existing, error: existingError } = await supabase
    .from('assessments')
    .select(
      'id, property_id, checklist_version, status, started_at, completed_at, updated_at',
    )
    .eq('owner_id', ownerId)
    .eq('property_id', propertyId)
    .in('status', ['draft', 'in_progress'])
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existingError) {
    throw existingError;
  }

  if (existing && existing.checklist_version === CHECKLIST_VERSION) {
    return existing as AssessmentRecord;
  }

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('assessments')
    .insert({
      owner_id: ownerId,
      property_id: propertyId,
      checklist_version: CHECKLIST_VERSION,
      status: 'in_progress',
      started_at: now,
    })
    .select(
      'id, property_id, checklist_version, status, started_at, completed_at, updated_at',
    )
    .single();

  if (error) {
    throw error;
  }

  return data as AssessmentRecord;
}

export async function loadInspectionResponses(
  ownerId: string,
  assessmentId: string,
): Promise<Record<string, InspectionResponseRecord>> {
  const { data, error } = await supabase
    .from('responses')
    .select(
      'id, assessment_id, question_id, answer, note, severity, updated_at',
    )
    .eq('owner_id', ownerId)
    .eq('assessment_id', assessmentId);

  if (error) {
    throw error;
  }

  return Object.fromEntries(
    ((data ?? []) as InspectionResponseRecord[]).map((response) => [
      response.question_id,
      response,
    ]),
  );
}

export async function saveInspectionResponse(
  ownerId: string,
  assessmentId: string,
  check: InspectionCheck,
  answer: InspectionAnswer,
  note: string | null = null,
): Promise<InspectionResponseRecord> {
  const severity =
    answer === 'issue_found' || answer === 'not_sure'
      ? severityToResponseSeverity(check.severity)
      : null;

  const { data, error } = await supabase
    .from('responses')
    .upsert(
      {
        owner_id: ownerId,
        assessment_id: assessmentId,
        question_id: check.id,
        answer,
        note,
        severity,
      },
      { onConflict: 'assessment_id,question_id' },
    )
    .select(
      'id, assessment_id, question_id, answer, note, severity, updated_at',
    )
    .single();

  if (error) {
    throw error;
  }

  const { error: assessmentError } = await supabase
    .from('assessments')
    .update({ status: 'in_progress' })
    .eq('id', assessmentId)
    .eq('owner_id', ownerId);

  if (assessmentError) {
    throw assessmentError;
  }

  return data as InspectionResponseRecord;
}
